import {
  IonButtons,
  IonModal,
  IonTitle,
  IonToolbar,
  IonHeader,
  IonContent,
  IonItem,
  IonLabel,
  useIonToast,
  IonText,
} from '@ionic/react';
import { SnapshotOut } from 'mobx-state-tree';
import { UserSN } from '../../models/User';
import {
  ComponentProps,
  FunctionComponent,
  useEffect,
  useRef,
  useState,
  useCallback,
} from 'react';
import Trans from 'util/Trans';
import DynamicButton from 'components/buttons/DynamicButton';
import { useUnitConversion } from 'hooks/useUnitConversion';
import _ from 'lodash';
import { MESSAGE_ALIVE_MS, MESSAGE_POSITION } from 'util/constants';

export interface BoardEditModalProps extends ComponentProps<typeof IonModal> {
  onCommit: () => Promise<void>;
  user?: SnapshotOut<UserSN>;
}

interface FormData {
  length: string;
  width: string;
  nose: string;
  tail: string;
  holes: string;
  wheelbase: string;
  wheels: string;
  weight: string;
  manufacturer: string;
  model: string;
  deckImg: string;
}

interface ValidationErrors {
  manufacturer?: string;
  model?: string;
}

const checkIfValid = (formData?: FormData): boolean => {
  return !!(formData && formData.manufacturer && formData.model);
};

const BoardEditModal: FunctionComponent<BoardEditModalProps> = ({
  onCommit,
  user,
  ...rest
}) => {
  const modalRef = useRef<HTMLIonModalElement>(null);
  const [present] = useIonToast();
  const [isCommitting, setIsCommitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>(
    {}
  );

  // Add unit conversion hook
  const unitConversion = useUnitConversion();

  // Initialize form data from user.board prop
  const initFormData = useCallback((): FormData => {
    const board = user?.board;

    // Helper function to convert board measurements
    const convertBoardMeasurement = (
      value: number | undefined,
      isWeight = false,
      isWheels = false
    ): string => {
      if (!value) return '';
      let displayValue: number;
      if (unitConversion.userUnits === 'metric') {
        displayValue = value;
      } else {
        if (isWeight) {
          displayValue = unitConversion.convert(
            value,
            'weight',
            'metric',
            'imperial'
          );
        } else if (isWheels) {
          displayValue = value / 25.4; // mm to inch
        } else {
          displayValue = value / 2.54; // cm to inch
        }
      }
      return displayValue.toFixed(2);
    };

    return {
      length: convertBoardMeasurement(board?.length),
      width: convertBoardMeasurement(board?.width),
      nose: convertBoardMeasurement(board?.nose),
      tail: convertBoardMeasurement(board?.tail),
      holes: convertBoardMeasurement(board?.holes),
      wheelbase: convertBoardMeasurement(board?.wheelbase),
      wheels: convertBoardMeasurement(board?.wheels, false, true),
      weight: convertBoardMeasurement(board?.weight, true),
      manufacturer: board?.manufacturer || '',
      model: board?.model || '',
      deckImg: board?.deckImg || '',
    };
  }, [user, unitConversion]);

  const [formData, setFormData] = useState<FormData>(initFormData);

  // Update form data when user prop or unit conversion changes
  useEffect(() => {
    setFormData(initFormData());
  }, [user?.board, unitConversion.userUnits]);

  const [isValidInput, setIsValidInput] = useState(() =>
    checkIfValid(formData)
  );

  // Validate form data whenever it changes
  useEffect(() => {
    setIsValidInput(
      checkIfValid(formData) && Object.keys(validationErrors).length === 0
    );
  }, [formData, validationErrors]);

  const dismiss = () => {
    modalRef.current?.dismiss();
  };

  const validateField = (
    fieldName: keyof ValidationErrors,
    value: string
  ): void => {
    const newErrors = { ...validationErrors };

    // Check required fields
    const requiredFields: (keyof ValidationErrors)[] = [
      'manufacturer',
      'model',
    ];

    if (requiredFields.includes(fieldName) && !value.trim()) {
      newErrors[fieldName] = (
        <Trans i18nKey="FIELD_REQUIRED">This field is required</Trans>
      ) as any;
    } else {
      delete newErrors[fieldName];
    }

    setValidationErrors(newErrors);
  };

  const updateBoard = (): void => {
    if (!user?.board) return;

    // Update board object with form data, converting imperial back to metric
    const board = user.board as any;
    board.manufacturer = formData.manufacturer;
    board.model = formData.model;
    board.deckImg = formData.deckImg;

    // Helper function to convert measurements back to metric
    const convertToMetric = (
      value: string,
      isWeight = false,
      isWheels = false
    ): number | undefined => {
      if (!value) return undefined;
      const numValue = parseFloat(value);
      if (isNaN(numValue)) return undefined;

      if (unitConversion.userUnits === 'metric') {
        return numValue;
      } else {
        // Convert from imperial to metric
        if (isWeight) {
          // lbs to kg
          return unitConversion.convert(
            numValue,
            'weight',
            'imperial',
            'metric'
          );
        } else if (isWheels) {
          // inches to mm
          return numValue * 25.4;
        } else {
          // inches to cm
          return numValue * 2.54;
        }
      }
    };

    const length = convertToMetric(formData.length);
    const width = convertToMetric(formData.width);
    const nose = convertToMetric(formData.nose);
    const tail = convertToMetric(formData.tail);
    const holes = convertToMetric(formData.holes);
    const wheelbase = convertToMetric(formData.wheelbase);
    const wheels = convertToMetric(formData.wheels, false, true);
    const weight = convertToMetric(formData.weight, true);

    if (length !== undefined) board.length = length;
    if (width !== undefined) board.width = width;
    if (nose !== undefined) board.nose = nose;
    if (tail !== undefined) board.tail = tail;
    if (holes !== undefined) board.holes = holes;
    if (wheelbase !== undefined) board.wheelbase = wheelbase;
    if (wheels !== undefined) board.wheels = wheels;
    if (weight !== undefined) board.weight = weight;
  };

  const _onCommit = () => {
    // Check for missing required fields and show warnings
    const missingFields = [];

    if (!formData.manufacturer.trim()) {
      missingFields.push('Hersteller');
    }
    if (!formData.model.trim()) {
      missingFields.push('Modell');
    }

    // Show warning if there are missing required fields
    if (missingFields.length > 0) {
      present({
        message: `Warnung: Folgende Pflichtfelder fehlen: ${missingFields.join(
          ', '
        )}. Bitte füllen Sie diese aus.`,
        duration: MESSAGE_ALIVE_MS,
        color: 'warning',
        position: MESSAGE_POSITION,
      });
      return; // Don't proceed with commit
    }

    setIsCommitting(true);
    updateBoard();
    onCommit().finally(() => {
      setIsCommitting(false);
    });
  };

  return (
    <IonModal {...rest} ref={modalRef} className="edit-modal">
      {/* Header like RiderEditModal */}
      <IonHeader className="ion-no-border">
        {/* <div className="modal-image-svg">
          <img src={'/assets/images/Pic_Continent_Filter.svg'} />
        </div> */}
        <IonToolbar className="edit-modal-toptoolbar">
          <IonTitle style={{ textAlign: 'center' }}>
            <Trans i18nKey="EDIT_BOARD">Board Bearbeiten</Trans>
          </IonTitle>
          <IonButtons slot="start">
            <DynamicButton
              text={<Trans i18nKey="OK" />}
              iconName="OKUpIcon"
              buttonType="icon-full"
              size="xlarge"
              fill="clear"
              className="spnx-button"
              disabled={isCommitting || !isValidInput}
              onClick={_onCommit}
            />
          </IonButtons>
          <IonButtons slot="end">
            <DynamicButton
              text={<Trans i18nKey="CANCEL" />}
              iconName="CancelUpIcon"
              buttonType="icon-full"
              size="xlarge"
              fill="clear"
              className="spnx-button"
              onClick={dismiss}
            />
          </IonButtons>
        </IonToolbar>
      </IonHeader>

      {/* Scrollable content like RiderEditModal */}
      <IonContent
        style={{
          '--background': 'transparent',
          '--border-colour': 0,
        }}
      >
        <div className="edit-modal-wrapper">
          <div className="edit-modal-group">
            <IonText>
              <Trans i18nKey="BOARD_INFO">Board Information</Trans>
            </IonText>
            <div className="edit-modal-innergroup">
              {/* Manufacturer Field */}
              <IonItem>
                <IonLabel position="stacked">
                  <Trans i18nKey="MANUFACTURER" />
                </IonLabel>
                <input
                  type="text"
                  value={formData.manufacturer}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const value = e.target.value || '';
                    setFormData((prev) => ({ ...prev, manufacturer: value }));
                    validateField('manufacturer', value);
                  }}
                  placeholder="Manufacturer"
                />
                {validationErrors.manufacturer && (
                  <span className="error-note">
                    {validationErrors.manufacturer}
                  </span>
                )}
              </IonItem>

              {/* Model Field */}
              <IonItem>
                <IonLabel position="stacked">
                  <Trans i18nKey="MODEL" />
                </IonLabel>
                <input
                  type="text"
                  value={formData.model}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const value = e.target.value || '';
                    setFormData((prev) => ({ ...prev, model: value }));
                    validateField('model', value);
                  }}
                  placeholder="Model"
                />
                {validationErrors.model && (
                  <span className="error-note">{validationErrors.model}</span>
                )}
              </IonItem>
            </div>
          </div>

          <div className="edit-modal-group">
            <IonText>
              <Trans i18nKey="SPECIFICATIONS">Spezifikationen</Trans>
            </IonText>
            <div className="edit-modal-innergroup">
              {/* Length Field */}
              <IonItem>
                <IonLabel position="stacked">
                  <Trans i18nKey="LENGTH" /> (
                  {unitConversion.userUnits === 'metric' ? 'cm' : 'in'})
                </IonLabel>
                <input
                  type="number"
                  value={formData.length}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const value = e.target.value || '';
                    setFormData((prev) => ({ ...prev, length: value }));
                  }}
                  placeholder={`Length in ${unitConversion.userUnits === 'metric' ? 'cm' : 'in'}`}
                  step="0.01"
                  inputMode="numeric"
                  pattern="[0-9]*"
                />
              </IonItem>

              {/* Width Field */}
              <IonItem>
                <IonLabel position="stacked">
                  <Trans i18nKey="WIDTH" /> (
                  {unitConversion.userUnits === 'metric' ? 'cm' : 'in'})
                </IonLabel>
                <input
                  type="number"
                  value={formData.width}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const value = e.target.value || '';
                    setFormData((prev) => ({ ...prev, width: value }));
                  }}
                  placeholder={`Width in ${unitConversion.userUnits === 'metric' ? 'cm' : 'in'}`}
                  step="0.01"
                  inputMode="numeric"
                  pattern="[0-9]*"
                />
              </IonItem>

              <IonItem>
                <IonLabel position="stacked">
                  <Trans i18nKey="NOSE" /> (
                  {unitConversion.userUnits === 'metric' ? 'cm' : 'in'})
                </IonLabel>
                <input
                  type="number"
                  value={formData.nose}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const value = e.target.value || '';
                    setFormData((prev) => ({ ...prev, nose: value }));
                  }}
                  placeholder={`Nose in ${unitConversion.userUnits === 'metric' ? 'cm' : 'in'}`}
                  step="0.01"
                  inputMode="numeric"
                  pattern="[0-9]*"
                />
              </IonItem>

              {/* Tail Field */}
              <IonItem>
                <IonLabel position="stacked">
                  <Trans i18nKey="TAIL" /> (
                  {unitConversion.userUnits === 'metric' ? 'cm' : 'in'})
                </IonLabel>
                <input
                  type="number"
                  value={formData.tail}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const value = e.target.value || '';
                    setFormData((prev) => ({ ...prev, tail: value }));
                  }}
                  placeholder={`Tail in ${unitConversion.userUnits === 'metric' ? 'cm' : 'in'}`}
                  step="0.01"
                  inputMode="numeric"
                  pattern="[0-9]*"
                />
              </IonItem>

              {/* Holes Field */}
              <IonItem>
                <IonLabel position="stacked">
                  <Trans i18nKey="HOLES" /> (
                  {unitConversion.userUnits === 'metric' ? 'cm' : 'in'})
                </IonLabel>
                <input
                  type="number"
                  value={formData.holes}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const value = e.target.value || '';
                    setFormData((prev) => ({ ...prev, holes: value }));
                  }}
                  placeholder={`Holes in ${unitConversion.userUnits === 'metric' ? 'cm' : 'in'}`}
                  step="0.01"
                  inputMode="numeric"
                  pattern="[0-9]*"
                />
              </IonItem>

              {/* Wheelbase Field */}
              <IonItem>
                <IonLabel position="stacked">
                  <Trans i18nKey="WHEELBASE" /> (
                  {unitConversion.userUnits === 'metric' ? 'cm' : 'in'})
                </IonLabel>
                <input
                  type="number"
                  value={formData.wheelbase}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const value = e.target.value || '';
                    setFormData((prev) => ({ ...prev, wheelbase: value }));
                  }}
                  placeholder={`Wheelbase in ${unitConversion.userUnits === 'metric' ? 'cm' : 'in'}`}
                  step="0.01"
                  inputMode="numeric"
                  pattern="[0-9]*"
                />
              </IonItem>

              {/* Wheels Field */}
              <IonItem>
                <IonLabel position="stacked">
                  <Trans i18nKey="WHEELS" /> (mm)
                </IonLabel>
                <input
                  type="number"
                  value={formData.wheels}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    let value = e.target.value || '';
                    // Nur ganze Zahlen erlauben
                    value = value.replace(/[^\d]/g, '');
                    setFormData((prev) => ({ ...prev, wheels: value }));
                  }}
                  placeholder="Wheel diameter"
                  step="1"
                  inputMode="numeric"
                  pattern="[0-9]*"
                />
              </IonItem>

              {/* Weight Field */}
              <IonItem>
                <IonLabel position="stacked">
                  <Trans i18nKey="WEIGHT" /> (
                  {unitConversion.getUnitLabel('weight')})
                </IonLabel>
                <input
                  type="number"
                  value={formData.weight}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const value = e.target.value || '';
                    setFormData((prev) => ({ ...prev, weight: value }));
                  }}
                  placeholder={`Weight in ${unitConversion.getUnitLabel('weight')}`}
                  step="0.01"
                  inputMode="numeric"
                  pattern="[0-9]*"
                />
              </IonItem>
            </div>
          </div>
        </div>
      </IonContent>
    </IonModal>
  );
};

export default BoardEditModal;
